/**
 * Gallery Variants Store - Variant selection and management
 */

import { formatPrice as formatPriceUtil } from '../services/pricing/price-utils.js';
import { getVariantImageUrls } from '../services/image/image-utils.js';

export function createGalleryVariantsStore() {
  return {
    formatPrice(price) {
      return formatPriceUtil(price);
    },

    /**
     * Set variant option with reactive updates
     * Single source of truth for variant selection
     */
    setVariantOption(type, value, options = {}) {
      const core_store = Alpine.store('galleryCore');
      const memory_store = Alpine.store('galleryMemory');

      const previousValue = core_store.variant_selection[type];
      if (previousValue === value) {
        return;
      }

      core_store.variant_selection[type] = value;

      if (memory_store.variant_lookup_cache.size > 0) {
        memory_store.variant_lookup_cache.clear();
      }

      const { size, frame, mount } = core_store.variant_selection;
      core_store.variant_selection.locked = !!(size && frame && mount);

      document.dispatchEvent(new CustomEvent(`gallery-${type}-changed`, {
        detail: {
          type,
          value,
          previousValue,
          selection: { ...core_store.variant_selection }
        }
      }));

      // Skip preview update if this is an initialization call (e.g., setting defaults on modal open)
      if (!options.skipPreviewUpdate) {
        Alpine.nextTick(() => {
          if (type === 'frame' || type === 'mount') {
            this.updatePreviewVariants(type);
          }
        });
      }
    },

    findVariantForArtwork(artwork, config = null) {
      const core_store = Alpine.store('galleryCore');
      const memory_store = Alpine.store('galleryMemory');

      if (!artwork || !core_store.variant_manager) {
        return null;
      }

      const selection = config || core_store.variant_selection;
      const cache_key = `${artwork.id}-${selection.size}-${selection.frame}-${selection.mount}`;

      if (memory_store.variant_lookup_cache.has(cache_key)) {
        memory_store.cache_hits++;
        memory_store.updateCacheAccessOrder(cache_key);
        return memory_store.variant_lookup_cache.get(cache_key);
      }

      memory_store.cache_misses++;

      // The findVariant method expects a config object
      const variant = core_store.variant_manager.findVariant(
        artwork,
        {
          size: selection.size,
          frame: selection.frame,
          mount: selection.mount,
          mode: 'exact'  // Use exact mode for cart items
        }
      );


      if (variant) {
        memory_store.variant_lookup_cache.set(cache_key, variant);
        memory_store.clearOldestCacheEntriesIfNeeded();
      }

      return variant;
    },

    /**
     * Apply preview variant configuration to a single artwork
     * Used for initial display - defaults to Exhibition/Black/Border for images
     */
    applyPreviewVariantToArtwork(artwork, position) {
      const core_store = Alpine.store('galleryCore');

      if (!core_store.variant_manager) {
        core_store.initVariantManager();
      }

      // Use defaults for initial display if no selections made
      const config = {
        size: core_store.variant_selection.size || 'Exhibition',
        frame: core_store.variant_selection.frame || 'Black',
        mount: core_store.variant_selection.mount || 'Border'
      };

      const variant = core_store.variant_manager.findVariant(artwork, config);

      if (variant) {
        artwork.selected_variant = { ...variant, product_id: artwork.id };
        artwork.preview_variant = { ...variant, product_id: artwork.id };
        artwork.variant_selected = true;

        core_store.markArtworkImageDirty?.(artwork.id);
      } else {
        const fallbackVariant = core_store.variant_manager.findFallbackVariant(artwork, {
          frame: core_store.variant_selection.frame,
          mount: core_store.variant_selection.mount,
          requireImage: true
        });

        if (fallbackVariant) {
          artwork.selected_variant = { ...fallbackVariant, product_id: artwork.id };
          artwork.preview_variant = { ...fallbackVariant, product_id: artwork.id };
          artwork.variant_selected = true;

          core_store.markArtworkImageDirty?.(artwork.id);
        }
      }
    },

    /**
     * Set a default variant for artwork when no configuration is available
     */
    setDefaultVariantForArtwork(artwork) {
      const core_store = Alpine.store('galleryCore');

      if (!core_store.variant_manager) {
        core_store.initVariantManager();
      }

      const defaultVariant = core_store.variant_manager.findDefaultVariant(artwork);

      if (defaultVariant) {
        artwork.selected_variant = { ...defaultVariant, product_id: artwork.id };
        artwork.variant_selected = true;
        core_store.markArtworkImageDirty?.(artwork.id);
      }
    },

    /**
     * Apply variant configuration to all artworks
     */
    async applyBulkVariantConfig(config) {
      const core_store = Alpine.store('galleryCore');

      core_store.initVariantManager();

      try {
        // First update the variant selection state
        core_store.variant_selection.size = config.size;
        core_store.variant_selection.frame = config.frame;
        core_store.variant_selection.mount = config.mount;
        core_store.variant_selection.locked = true;

        // Apply configuration through variant manager
        await core_store.variant_manager.applyBulkConfiguration(
          core_store.selected_artworks,
          config
        );

        // Now re-apply variants to all selected artworks with the new configuration
        core_store.selected_artworks.forEach((artwork, index) => {
          if (artwork) {
            const variant = this.findVariantForArtwork(artwork, config);
            if (variant) {
              artwork.selected_variant = variant;
              artwork.variant_selected = true;
              core_store.markArtworkImageDirty?.(artwork.id);
            }
          }
        });

        return true;
      } catch (error) {
        console.error('Failed to apply variant configuration:', error);
        return false;
      }
    },

    /**
     * Apply saved variant to new artwork
     */
    applyVariantToArtwork(artwork, position) {
      const core_store = Alpine.store('galleryCore');

      if (!core_store.variant_selection.locked || !core_store.variant_selection.size) {
        return;
      }

      core_store.initVariantManager();

      const variant = core_store.variant_manager.findVariant(artwork, {
        size: core_store.variant_selection.size,
        frame: core_store.variant_selection.frame,
        mount: core_store.variant_selection.mount
      });

      if (variant) {
        artwork.selected_variant = variant;
        artwork.variant_selected = true;
        core_store.markArtworkImageDirty?.(artwork.id);
      }
    },

    /**
     * Update preview variants based on current preview config
     */
    async updatePreviewVariants(changed_option) {
      const core_store = Alpine.store('galleryCore');
      const memory_store = Alpine.store('galleryMemory');

      const start_time = performance.now();

      if (!core_store.variant_manager) {
        core_store.initVariantManager();
      }

      const new_variants = core_store.selected_artworks.map(artwork => {
        if (!artwork) return null;
        const variant = core_store.variant_manager.findVariant(artwork, {
          ...core_store.variant_selection,
          mode: 'preview'
        });
        return variant;
      }).filter(Boolean);

      if (changed_option === 'size') {
        core_store.preview_variants = new_variants;
        return;
      }

      if (changed_option === 'frame' || changed_option === 'mount') {
        const image_urls = new Set();

        new_variants.forEach(v => {
          if (v?.featured_image?.src) {
            getVariantImageUrls(v).forEach(url => image_urls.add(url));
          }
        });

        const urls_array = Array.from(image_urls);

        const cached_count = urls_array.filter(url =>
          memory_store.isImagePreloaded(url, core_store.orientation)
        ).length;

        if (cached_count === urls_array.length) {
          core_store.preview_variants = new_variants;
          return;
        }

        core_store.preview_loading = true;

        try {
          memory_store.incrementImageLoads(urls_array.length - cached_count);

          await new Promise(resolve => {
            requestAnimationFrame(async () => {
              await memory_store.preloadImageBatch(urls_array, core_store.orientation);
              resolve();
            });
          });

          await Alpine.nextTick();
        } catch (error) {
          console.error('Error loading preview images:', error);
        }
      }

      // Batch update all state together to ensure atomic image updates
      await new Promise(resolve => {
        requestAnimationFrame(() => {
          // Update preview_variants first
          core_store.preview_variants = new_variants;

          // Mark all images dirty in single pass
          core_store.selected_artworks.forEach(artwork => {
            if (!artwork) return;
            const previewVariant = core_store.variant_manager.findPreviewByProductId(new_variants, artwork.id);
            if (previewVariant?.featured_image?.src) {
              core_store.markArtworkImageDirty?.(artwork.id);
            }
          });

          // Wait for browser to update DOM
          requestAnimationFrame(resolve);
        });
      });

      // Small delay to ensure all images have rendered together
      await new Promise(resolve => setTimeout(resolve, 50));

      core_store.preview_loading = false;
    },

    /**
     * Prepare artworks with variant images before they're set as selected
     */
    prepareArtworksWithVariants(artworks, saved_config) {
      const core_store = Alpine.store('galleryCore');

      if (!artworks || artworks.length === 0) return;

      if (!core_store.variant_manager) {
        core_store.initVariantManager();
      }

      artworks.forEach((artwork, index) => {
        if (!artwork) return;

        const variant = core_store.variant_manager.findVariant(artwork, {
          size: saved_config.size,
          frame: saved_config.frame,
          mount: saved_config.mount,
          mode: 'preview'
        });

        if (variant && variant.featured_image?.src) {
          artwork.selected_variant = variant;
          artwork.variant_selected = true;
        }
      });
    },

    /**
     * Apply saved variant configuration to restored artworks
     */
    applyVariantsToRestoredArtworks(saved_config) {
      const core_store = Alpine.store('galleryCore');

      if (!core_store.selected_artworks || core_store.selected_artworks.length === 0) return;

      core_store.initVariantManager();

      core_store.selected_artworks.forEach((artwork, index) => {
        if (!artwork) return;

        if (saved_config.locked) {
          this.applyVariantToArtwork(artwork, index);
        }
        else if (saved_config.size || saved_config.frame || saved_config.mount) {
          const variant = core_store.variant_manager.findVariant(artwork, {
            ...saved_config,
            mode: 'preview'
          });

          if (variant) {
            artwork.selected_variant = variant;
            artwork.variant_selected = true;
          }
        }
      });

      if (saved_config.size || saved_config.frame || saved_config.mount) {
        core_store.variant_selection.size = saved_config.size;
        core_store.variant_selection.frame = saved_config.frame;
        core_store.variant_selection.mount = saved_config.mount;
        core_store.variant_selection.locked = saved_config.locked || false;
      }
    },

    /**
     * Prepare cart payload with selected variants
     */
    prepareCartPayload() {
      const core_store = Alpine.store('galleryCore');
      const { size, frame, mount } = core_store.variant_selection;

      // Check if variant manager needs to be initialized
      if (!core_store.variant_manager) {
        core_store.initVariantManager();
      }

      if (!(size && frame && mount)) {
        return [];
      }

      if (!core_store.variant_manager) {
        return [];
      }

      const cart_items = [];
      const layout_type = core_store.layout_type;
      const set_id = Date.now();

      core_store.selected_artworks.forEach((artwork, index) => {
        if (!artwork) return;

        const variant = this.findVariantForArtwork(artwork);

        if (variant) {
          cart_items.push({
            id: variant.id,
            quantity: 1,
            properties: {
              '_gallery_set': layout_type,
              '_gallery_position': index + 1,
              '_gallery_set_id': set_id,
              '_artwork_title': artwork.title,
              '_artist': artwork.vendor || '',
              '_size': core_store.variant_selection.size,
              '_frame': core_store.variant_selection.frame,
              '_mount': core_store.variant_selection.mount
            }
          });
        }
      });


      document.dispatchEvent(new CustomEvent('gallery-cart-prepared', {
        detail: {
          items: cart_items,
          total: core_store.computed_total_price,
          configuration: { ...core_store.variant_selection }
        }
      }));

      return cart_items;
    }
  };
}
